"use client";

import { useTina, tinaField, coloredText } from "@/lib/utils";
import Section from "@/components/Section";
import Pill from "@/components/Pill";
import CallToAction from "@/components/CallToAction";

import { Button } from "@/components/ui/button";
import Link from "@/components/ui/link";

export default function PricingPage(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const { header, pricingPackages, specials, callToAction } = data.pricing;

  return (
    <div>
      <Header header={header} />
      <Section
        wrapperClassName="bg-gradient-to-t from-background-purplish to-background-blackish"
        className="flex flex-col gap-24 lg:gap-40"
      >
        <PricingPackages pricingPackages={pricingPackages} />
        <Specials specials={specials} />
      </Section>

      <CallToAction callToAction={callToAction} />
    </div>
  );
}

const Header = ({ header }) => {
  const title = coloredText(header?.title);
  return (
    <Section
      as="header"
      wrapperClassName="!pb-0"
      className="flex flex-col gap-12 text-center"
    >
      <h1
        data-tina-field={tinaField(header, "title")}
        dangerouslySetInnerHTML={{ __html: title }}
      />
    </Section>
  );
};

const PricingPackages = ({ pricingPackages }) => {
  const packages = pricingPackages?.packages;
  const subtitle = pricingPackages?.subtitle;
  const description = pricingPackages?.description;
  return (
    <div className="flex flex-col gap-12 text-center">
      <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-4">
        {packages?.map((p, index) => (
          <div
            key={index}
            className="card bg-background-purplish flex flex-col items-center gap-8 text-center"
          >
            <div className="border-2 border-primary-300 rounded-full w-10 h-10 flex items-center justify-center text-primary-300 text-xl font-bold font-title">
              {index + 1}
            </div>

            <div className="space-y-2">
              <h2
                className="h6 uppercase"
                data-tina-field={tinaField(
                  pricingPackages,
                  `packages.${index}.title`
                )}
              >
                {p.title}
              </h2>
              <p
                className="text-foreground-500 "
                data-tina-field={tinaField(
                  pricingPackages,
                  `packages.${index}.subtitle`
                )}
              >
                {p.subtitle}
              </p>
            </div>
            <div className="space-y-3">
              <p
                className="h2 text-primary-300"
                data-tina-field={tinaField(
                  pricingPackages,
                  `packages.${index}.price`
                )}
              >
                {p.price}
              </p>
              <p
                className="text-foreground-100 h6 uppercase"
                data-tina-field={tinaField(
                  pricingPackages,
                  `packages.${index}.discount`
                )}
              >
                {p.discount}
              </p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-6">
        <h2
          className="h3"
          data-tina-field={tinaField(pricingPackages, "subtitle")}
        >
          {subtitle}
        </h2>
        <p
          className="p-xl text-foreground-100 max-w-3xl mx-auto"
          data-tina-field={tinaField(pricingPackages, "description")}
        >
          {description}
        </p>
      </div>
    </div>
  );
};

const Specials = ({ specials }) => {
  return (
    <div className="flex flex-col gap-4 lg:gap-8 text-center">
      <div className="flex flex-col items-center gap-6">
        <Pill data-tina-field={tinaField(specials, "sectionTitle")}>
          {specials?.sectionTitle}
        </Pill>
        <h2 data-tina-field={tinaField(specials, "title")}>
          {specials?.title}
        </h2>
      </div>
      <p data-tina-field={tinaField(specials, "description")}>
        {specials?.description}
      </p>
      <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4">
        {specials?.items?.map((item, index) => (
          <div key={index} className="card bg-background-blackish">
            <h3
              className="h5"
              data-tina-field={tinaField(specials, `items.${index}.title`)}
            >
              {item.title}
            </h3>
            <p
              className="text-foreground-500 mt-3 mb-6"
              data-tina-field={tinaField(
                specials,
                `items.${index}.description`
              )}
            >
              {item.description}
            </p>
            <Button
              asChild
              data-tina-field={tinaField(specials, `items.${index}.button`)}
            >
              <Link href={item?.button?.link || "/"}>
                {item?.button?.label}
              </Link>
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};
